'use client'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { usePrivy } from '@privy-io/react-auth'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useAccount } from 'wagmi'
import { useCardEligibility } from '../hooks/useCardEligibility'
import { CardEligibilityResult } from '../types/CardState'

interface CardEligibilityCheckProps {
  onEligibilityChecked: (result: CardEligibilityResult) => void
}

export function CardEligibilityCheck({ onEligibilityChecked }: CardEligibilityCheckProps) {
  const { address } = useAccount()
  const { connectWallet } = usePrivy()
  const { checkEligibility, isLoading, error, reset } = useCardEligibility()
  const searchParams = useSearchParams()
  const referralCode = searchParams.get('ref')?.trim() || undefined

  const handleButtonClick = async () => {
    if (!address) {
      connectWallet()
      return
    }

    reset()

    try {
      const result = await checkEligibility(referralCode)
      onEligibilityChecked(result)
    } catch (err) {
      console.error('Error checking eligibility:', err)
    }
  }

  return (
    <div className="flex flex-col-reverse items-center gap-8 lg:flex-row">
      <div className="flex flex-col gap-4">
        <Panel.Wrapper className="w-full max-w-xl">
          <Panel.Content className="flex flex-col gap-6 p-8">
            <div className="text-left">
              <Typography variant="h3" gradient>
                Get beta access to the card
              </Typography>
              <Typography variant="p" className="mt-2 text-muted-foreground">
                Sign in with your HyperEVM wallet to verify your eligibility. Users with enough points will be granted
                direct access to the card beta.
              </Typography>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex justify-start">
                <Button onClick={handleButtonClick} disabled={isLoading} className="min-w-[200px]">
                  <div className="flex items-center gap-2">
                    {!address ? (
                      'Connect Wallet'
                    ) : isLoading ? (
                      'Checking...'
                    ) : (
                      <>
                        <Image src="/tokens/hype.png" alt="HyperEVM" width={20} height={20} />
                        Sign In with HyperEVM
                      </>
                    )}
                  </div>
                </Button>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                  <Typography variant="p" className="text-red-400 text-sm">
                    {error.message?.includes('User rejected') || error.message?.includes('rejected')
                      ? 'Signature was cancelled. Please try again to check your eligibility.'
                      : error.message || 'Failed to check eligibility. Please try again.'}
                  </Typography>
                </div>
              )}
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      </div>
      <div className="flex justify-center">
        <Cards />
      </div>
    </div>
  )
}
